import { useState } from 'react';
import { findCharacteristicElement } from '../findCharacteristicElement';
import { pickLocale, type ActionButtonCharacteristic } from '../../schema';
import { useTranslation } from '../../i18n';
import { useRunValidation } from '../../validation/useValidation';
import { useSetValidationErrors, useStoreActions } from '../../state/StoreContext';
import { useEffectiveSelections } from '../../state/useEffectiveSelections';
import { useComponent } from '../../state/ComponentContext';
import { useWebshopContext } from '../../state/WebshopContext';
import { useRuleResults } from '../../rules/RuleResultsContext';
import { useEvaluatedMap } from '../../eval/EvalContext';
import { snapshotCombinedImage } from '../combinedImage/snapshot';
import { buildAssetPayload } from '../../api/assetPayload';
import { resolveToken } from '../../api/resolveToken';
import type { ConfiguratorPreviewContext } from '../../preview-context';
import type { ActionEvent, ActionEventResult } from '../../api/instance';
import { useInstanceId } from '../../state/InstanceContext';
import { getInstanceEventListeners } from '../../public-api';

type Status = 'idle' | 'busy' | 'success' | 'error' | 'invalid';

interface ActionButtonViewProps {
    characteristic: ActionButtonCharacteristic;
    locale: string | undefined;
}

/**
 * Action button (legacy wire type `'savebutton'`): runs client-side
 * validation, POSTs the current selections to `postUrl` if configured, and
 * optionally redirects on success. The button renders standalone — no
 * Characteristic-Shell title/info chrome above it; the button's own label
 * is the UI.
 */
export function ActionButtonView({ characteristic, locale }: ActionButtonViewProps) {
    const { t, locale: uiLocale } = useTranslation();
    const runValidation = useRunValidation();
    const setValidationErrors = useSetValidationErrors();
    const selectionsSnapshot = useEffectiveSelections();
    const component = useComponent();
    const webshop = useWebshopContext();
    const rules = useRuleResults();
    const evaluated = useEvaluatedMap();
    const { setCaptchaPayload } = useStoreActions();
    const instanceId = useInstanceId();
    const [status, setStatus] = useState<Status>('idle');
    const [errorMsg, setErrorMsg] = useState<string>('');
    // `event`: a message the page returned — shown as is, without the
    // "Submission failed:" frame the network errors get.
    const [pageMsg, setPageMsg] = useState<string>('');

    const label =
        pickLocale(characteristic.title, locale) ??
        characteristic.altTitle ??
        t('actionbutton.default_label');

    async function handleClick() {
        setErrorMsg('');
        setPageMsg('');

        const errors = runValidation();
        // Push errors into the store so each Characteristic surfaces its
        // own inline message — the action button stays a plain CTA, no
        // catch-all error blob beneath it.
        setValidationErrors(errors);
        if (errors.length > 0) {
            setStatus('invalid');
            // Scroll the first invalid Characteristic into view so the
            // customer doesn't have to hunt for it on long forms.
            const firstId = errors[0]?.characteristicId;
            if (typeof firstId === 'number' && typeof document !== 'undefined') {
                const el = findCharacteristicElement(firstId);
                el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
        }

        // `event`: nothing is sent anywhere — the page gets the payload.
        const handsOver = characteristic.saveAction === 'event';
        const postUrl = characteristic.postUrl ?? '';
        if (!handsOver && !postUrl) {
            // No endpoint configured. This is almost always a configuration
            // bug (Save-Button save_action set to a built-in like
            // `send_email`, but the SavebuttonActionPublisher hasn't filled
            // in the post_url yet — typically because the Cloud Service
            // didn't return one, or this code path was reached before the
            // V3 endpoint started rewriting in preview mode). Surface it
            // as a real error rather than fake-success so the admin
            // notices before publishing.
            setStatus('error');
            setErrorMsg(t('actionbutton.no_endpoint'));
            console.warn(
                '[Configento] ActionButton has no post_url configured — '
                + `characteristic ${characteristic.id} (${characteristic.altTitle}).`
                + ' The configurator can\'t POST anywhere.',
            );
            return;
        }

        setStatus('busy');
        let result: { ok: boolean; status: number; body: unknown; message: string; fromPage?: boolean };
        let handedOver: { prevented: boolean } | null = null;
        try {
            // Build the alt_title-keyed payload the legacy storefront
            // produces (configurator.js:generateHeaderValue). The savebutton
            // backend (SavebuttonActions\EmailSender::resolveRecipient,
            // PlaceholderRenderer::extractField) looks up fields by
            // alt_title — `payload['email']`, `payload['name']` etc. — and
            // expects each entry to carry `{value, label, valueLabel,
            // visibility, …}`. Posting raw `{selections: {1: ...}}` makes
            // the recipient lookup fail with HTTP 500 even when the form
            // validation passes.
            const payload = buildAssetPayload({
                component,
                selections: selectionsSnapshot,
                evaluated,
                locale,
                uiLocale,
                // What a rule has removed is not part of the order — the
                // entry stays (templates look up by alt_title) but goes out
                // blank with visibility 0.
                hiddenCharacteristics: rules.hiddenCharacteristics,
            });
            // Snapshot the live combined-image stack and attach as base64
            // PNG. Legacy goes through a separate cloud-asset upload here
            // (`app/webshop.js:finalizeAction` + `cloudasset.js`); our
            // savebutton backend is self-hosted so we can inline the bytes
            // — saves a round trip and keeps the payload self-contained.
            // Returns null + skips the field when no combined image is
            // configured or the canvas would be tainted.
            const combinedImage = await snapshotCombinedImage(
                component,
                selectionsSnapshot,
            );
            if (combinedImage) {
                payload._combined_image = combinedImage;
            }

            if (handsOver) {
                const emitted = emitAction(instanceId, {
                    ok: true,
                    status: 0,
                    body: null,
                    characteristic: characteristic.altTitle ?? String(characteristic.id),
                    action: 'event',
                    payload,
                });
                handedOver = emitted;
                result = await settleHandOver(emitted, t('actionbutton.no_handler'));
            } else {
                // Built-in actions (/action/pdf|email/{ref}) and custom_url
                // take different paths:
                //  - Built-in: the URL points at our own backend (admin host).
                //    POST directly — same-origin from the bundle's POV (the
                //    bundle and the action route share configento.app), CORS
                //    handled by AddCloudCorsHeaders. Captcha is verified inline.
                //  - Custom URL: the merchant's URL is on an arbitrary host
                //    that won't have CORS set up for the storefront origin.
                //    Route through the cloud proxy
                //    `cloud.configento.app/api/v3/components/{id}/savebutton/custom-url`,
                //    which re-validates token + origin + captcha and POSTs to
                //    the merchant server-side. The bundle never touches the
                //    merchant URL directly.
                const isCustomUrl = !isBuiltinActionUrl(postUrl);
                const targetUrl = isCustomUrl
                    ? buildCustomUrlProxyUrl(webshop, component.id)
                    : postUrl;
                const targetBody = isCustomUrl
                    ? JSON.stringify({ characteristic_id: characteristic.id, payload })
                    : JSON.stringify(payload);
                const targetHeaders: Record<string, string> = { 'Content-Type': 'application/json' };
                if (isCustomUrl) {
                    // Same auth shape /api/v3/components/{id} uses — short-lived
                    // jscloud-tok resolved from the preview context (admin path)
                    // or fetched via tokenEndpoint (storefront plugin path).
                    if (!webshop) throw new Error('webshop context missing');
                    targetHeaders['x-jscloud-tok'] = await resolveToken(webshop);
                }
                const res = await fetch(targetUrl, {
                    method: 'POST',
                    headers: targetHeaders,
                    body: targetBody,
                });

                // generate_pdf actions stream the PDF bytes back in the response
                // body. Legacy cloud's storefront bundle discarded the body and
                // relied on a follow-up redirect; the V3 backend is self-hosted
                // so we materialise the blob and trigger a real browser download.
                // Detection is by Content-Type rather than by inspecting the
                // postUrl — keeps the View agnostic of route shape.
                const contentType = res.headers.get('Content-Type') ?? '';
                let body: unknown = null;
                if (res.ok && contentType.startsWith('application/pdf')) {
                    await triggerBlobDownload(res);
                } else {
                    body = await readBody(res, contentType);
                }
                result = { ok: res.ok, status: res.status, body, message: res.ok ? '' : `HTTP ${res.status}` };
            }
        } catch (err) {
            result = { ok: false, status: 0, body: null, message: err instanceof Error ? err.message : String(err) };
        } finally {
            // The server accepts each captcha solution once. Clearing it
            // makes CaptchaView solve a fresh one, so a second click (or a
            // retry after an error) doesn't fail with 403.
            for (const group of component.groups) {
                for (const c of group.characteristics) {
                    if (c.type === 'captcha') setCaptchaPayload(c.id, '');
                }
            }
        }

        // `event` already told the page; the other actions report now.
        const prevented = handedOver
            ? handedOver.prevented
            : emitAction(instanceId, {
                ok: result.ok,
                status: result.status,
                body: result.body,
                characteristic: characteristic.altTitle ?? String(characteristic.id),
                action: actionKind(postUrl),
            }).prevented;
        if (prevented) {
            setStatus('idle');
            return;
        }
        if (!result.ok) {
            setStatus('error');
            if (result.fromPage && result.message !== '') setPageMsg(result.message);
            else setErrorMsg(result.message);
            return;
        }
        if (result.fromPage) setPageMsg(result.message);
        setStatus('success');
        if (characteristic.redirectUrl) {
            window.location.href = characteristic.redirectUrl;
        }
    }

    const busy = status === 'busy';

    return (
        <div className="cfg-action-button-wrap" data-configento-action-status={status}>
            <button
                type="button"
                className="cfg-action-button"
                disabled={busy}
                onClick={handleClick}
            >
                {busy ? t('actionbutton.busy') : label}
            </button>
            {status === 'error' && (pageMsg || errorMsg) && (
                <p className="cfg-action-button__error" role="alert">
                    {pageMsg || t('actionbutton.network_error', { message: errorMsg })}
                </p>
            )}
            {status === 'success' && !characteristic.redirectUrl && (
                <p className="cfg-action-button__success" role="status">
                    {pageMsg || t('actionbutton.success')}
                </p>
            )}
        </div>
    );
}

/**
 * Convert the fetch response into a blob and click a hidden anchor to
 * surface the browser's "Save as…" UX. The filename is taken from the
 * Content-Disposition header (the action backend always sets one); we
 * fall back to a generic name if it's missing or malformed. The object
 * URL is revoked on the next tick — the click has fired synchronously
 * so the browser has already started reading the blob.
 */
async function triggerBlobDownload(res: Response): Promise<void> {
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filenameFromDisposition(res.headers.get('Content-Disposition'))
        ?? 'configurator.pdf';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // Give the browser a tick to consume the blob before revoking.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

interface Emitted {
    /** A listener called `preventDefault()` — read after the listeners ran. */
    readonly prevented: boolean;
    listeners: number;
    /** What the listeners returned, for action `event`. */
    returned: unknown[];
}

/**
 * Tell the host how the action ended (`on('action', …)`), or for `event`
 * hand it the payload.
 */
function emitAction(
    instanceId: string | undefined,
    result: Omit<ActionEvent, 'preventDefault' | 'defaultPrevented'>,
): Emitted {
    const listeners = instanceId ? getInstanceEventListeners(instanceId)?.action : undefined;
    let prevented = false;
    const emitted: Emitted = { get prevented() { return prevented; }, listeners: listeners?.size ?? 0, returned: [] };
    if (!listeners || listeners.size === 0) return emitted;
    const event: ActionEvent = {
        ...result,
        preventDefault: () => { prevented = true; },
        get defaultPrevented() { return prevented; },
    };
    for (const cb of listeners) {
        try { emitted.returned.push((cb as (e: ActionEvent) => unknown)(event)); }
        catch (err) {
            console.error('[Configento] action listener threw:', err);
            emitted.returned.push(Promise.reject(err));
        }
    }
    return emitted;
}

/**
 * The outcome of an `event` hand-over: what the page's listeners resolved
 * to. No listener is an error (nothing would happen); a rejection or a
 * `{ ok: false }` fails; a listener that returns nothing counts as done.
 */
async function settleHandOver(
    emitted: Emitted,
    noHandler: string,
): Promise<{ ok: boolean; status: number; body: unknown; message: string; fromPage: boolean }> {
    if (emitted.listeners === 0) {
        console.warn('[Configento] Action button "event" has no on(\'action\') listener on this page.');
        return { ok: false, status: 0, body: null, message: noHandler, fromPage: false };
    }
    const settled = await Promise.allSettled(emitted.returned.map((r) => Promise.resolve(r)));
    for (const s of settled) {
        if (s.status === 'rejected') {
            const message = s.reason instanceof Error ? s.reason.message : String(s.reason ?? '');
            return { ok: false, status: 0, body: null, message, fromPage: true };
        }
    }
    for (const s of settled) {
        const value = (s as PromiseFulfilledResult<unknown>).value as Partial<ActionEventResult> | undefined;
        if (value && typeof value === 'object' && typeof value.ok === 'boolean') {
            return { ok: value.ok, status: 0, body: null, message: typeof value.message === 'string' ? value.message : '', fromPage: true };
        }
    }
    return { ok: true, status: 0, body: null, message: '', fromPage: true };
}

/** The response as JSON when it parses, else as text; null when unreadable. */
async function readBody(res: Response, contentType: string): Promise<unknown> {
    const text = await res.text().catch(() => null);
    if (text === null || text === '') return null;
    if (contentType.includes('json') || /^\s*[{[]/.test(text)) {
        try { return JSON.parse(text); } catch { /* not JSON after all */ }
    }
    return text;
}

function actionKind(url: string): ActionEvent['action'] {
    const m = /\/action\/(pdf|email)\//.exec(url);
    return m ? (m[1] as 'pdf' | 'email') : 'custom_url';
}

/**
 * Built-in action URLs are served by ActionController on our own host —
 * `POST` directly against them is fine (same-origin from the bundle's
 * POV, CORS handled by AddCloudCorsHeaders, captcha verified inline by
 * the controller). Anything that doesn't match this shape is a merchant
 * URL on an arbitrary host and gets routed through the cloud proxy
 * instead — see `buildCustomUrlProxyUrl`.
 */
function isBuiltinActionUrl(url: string): boolean {
    return /\/action\/(pdf|email)\/[^/?#]+/.test(url);
}

/**
 * Build the cloud-side custom-URL proxy endpoint for a Component. The
 * bundle posts the savebutton's characteristic id + payload here; the
 * backend re-validates token + origin + captcha and forwards the call
 * to the merchant URL stored on the savebutton itself.
 */
function buildCustomUrlProxyUrl(
    webshop: ConfiguratorPreviewContext | undefined,
    componentId: number,
): string {
    const base = (webshop?.cloudBaseUrl ?? '').replace(/\/+$/, '');
    const prefix = base.startsWith('http') ? '' : 'https://';
    return `${prefix}${base}/api/v3/components/${componentId}/savebutton/custom-url`;
}

function filenameFromDisposition(disposition: string | null): string | null {
    if (!disposition) return null;
    // RFC 6266: filename* takes precedence over filename. Handles both.
    const utf8Match = disposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
    if (utf8Match) {
        try { return decodeURIComponent(utf8Match[1]!.trim()); } catch { /* fall through */ }
    }
    const plainMatch = disposition.match(/filename\s*=\s*"?([^";]+)"?/i);
    return plainMatch ? plainMatch[1]!.trim() : null;
}

